import { type ReactNode, useState, useMemo, useCallback, useLayoutEffect, useRef } from "react";
import { Card, CardContent } from "@/ui/base/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/ui/base/table";
import { TableSkeleton } from "@/ui/base/table-skeleton";
import { EmptyState } from "@/ui/EmptyState";
import { DataPagination } from "@/ui/DataPagination";
import { Checkbox } from "@/ui/base/checkbox";
import { type LucideIcon, ArrowUp, ArrowDown, ArrowUpDown, HelpCircle } from "lucide-react";
import { cn } from "@/nucleo/utilitarios";
import { TableViewControls } from "@/ui/table-view/TableViewControls";
import type { PreferenciasDaTabela } from "@/ui/table-view/useTableViewPreferences";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/ui/base/tooltip";
import { useTableDensity, type TableDensity } from "@/nucleo/ganchos/useTableDensity";
import { GlossaryTerm, type ChaveDoGlossario } from "@/ui/GlossaryTerm";

export type SortDirection = "asc" | "desc" | null;

export interface SortState {
  column: string | null;
  direction: SortDirection;
}

export interface DataTableColumn<T> {
  /** Stable id for personalization and robust sorting */
  id?: string;
  /** Header label */
  header: string;
  /** Key accessor or render function */
  accessorKey?: keyof T;
  /** Custom cell renderer */
  cell?: (row: T) => ReactNode;
  /** Additional className for the header */
  headerClassName?: string;
  /** Additional className for the cell */
  cellClassName?: string;
  /** Enable client-side sorting for this column. Provide an accessor key or function. */
  sortKey?: keyof T | ((row: T) => string | number | null | undefined);
  /** Marca a coluna como a coluna de seleção em lote (ganha checkbox tri-state automático no header). */
  isSelectionColumn?: boolean;
  /** Texto de ajuda exibido em tooltip (ícone HelpCircle ao lado do header). Útil para siglas/conceitos. */
  tooltip?: string;
  /**
   * Chave do catálogo `ui/GlossaryTerm.tsx` — quando presente, o texto do
   * header ganha sublinhado pontilhado e abre o mesmo popover de definição
   * usado na legenda da tela (clique, não só hover — acessível por teclado).
   * Ignorado se `tooltip` também estiver presente (não faz sentido os dois).
   */
  headerTermo?: ChaveDoGlossario;
}

interface DataTableProps<T extends { id: string }> {
  /** Column definitions */
  columns: DataTableColumn<T>[];
  /** Data rows */
  data: T[];
  /** Whether data is loading */
  isLoading?: boolean;
  /** Total count for pagination (if not provided, uses data.length) */
  total?: number;
  /** Current page */
  page?: number;
  /** Items per page */
  perPage?: number;
  /** Page change handler */
  onPageChange?: (page: number) => void;
  /** Per page change handler */
  onPerPageChange?: (perPage: number) => void;
  /** Row click handler */
  onRowClick?: (row: T) => void;
  /** Empty state props */
  emptyIcon?: LucideIcon;
  emptyTitle?: string;
  emptyDescription?: string;
  emptyActionLabel?: string;
  onEmptyAction?: () => void;
  /** Minimum width for the table (e.g. "640px") */
  minWidth?: string;
  /** aria-label for the table */
  ariaLabel?: string;
  /** Number of skeleton columns to show */
  skeletonColumns?: number;
  /** Custom header row (for checkboxes, etc.). Replaces the auto-generated header. */
  headerRow?: ReactNode;
  /** Custom row renderer (overrides default row generation) */
  renderRow?: (row: T) => ReactNode;
  /** Whether to show pagination (default: true when page/onPageChange provided) */
  showPagination?: boolean;
  /** IDs atualmente selecionados (para o checkbox tri-state do header). */
  selectedIds?: string[];
  /** Callback chamado ao clicar no checkbox do header — deve marcar/desmarcar todos os ids visíveis. */
  onTogglePageSelection?: () => void;
  /** Função opcional para extrair id da linha (default: row.id). */
  selectionGetId?: (row: T) => string;
  /** Classe CSS adicional por linha (ex.: destaque para itens críticos). */
  getRowClassName?: (row: T) => string | undefined;
  /** Override pontual de densidade. Quando omitido, usa preferência global em localStorage. */
  density?: TableDensity;
  /**
   * Marca que alguma célula desta tabela tem um controle focável de verdade
   * (ex.: `CopyableText`), fora do próprio `onRowClick`. Sem isto, a linha
   * vira `role="button"` + `tabIndex=0` E carrega um botão focável por
   * dentro — dois controles interativos aninhados, `nested-interactive` pro
   * axe (achado real em `/operacao/inscricoes`, com `CopyableText` na coluna
   * Protocolo). Com a marca, a linha some da ordem de tabulação e do papel
   * de widget (deixa de ser o alvo de teclado — continua clicável por
   * mouse); quem monta a tela então precisa dar à linha um caminho de
   * teclado de verdade, envolvendo a célula principal num `<Link>` real.
   */
  linhaComElementoInterativo?: boolean;
  /**
   * Personalizacao de colunas (mostrar, ocultar, reordenar): o retorno de
   * `useTableViewPreferences`. Quando presente, o botao "Personalizar colunas"
   * aparece SEMPRE no mesmo lugar — a ponta direita do cabecalho — e abre
   * sempre o mesmo modal. E o unico jeito de ligar o recurso: nenhuma tela poe
   * o botao numa barra acima da tabela nem traz popover proprio. Quem passa
   * isto tambem passa `columns={personalizacao.orderedColumns}`.
   */
  personalizacao?: PreferenciasDaTabela<T>;
}

/** Pura: nao depende de nada do componente, por isso fica fora dele (evita recriar a cada render). */
function getColumnId<T>(column: DataTableColumn<T>): string {
  return column.id ?? column.header;
}

function getSortValue<T>(row: T, sortKey: keyof T | ((row: T) => string | number | null | undefined)): { isNull: boolean; value: string | number } {
  const raw = typeof sortKey === "function" ? sortKey(row) : row[sortKey];
  if (raw == null || raw === "") return { isNull: true, value: "" };
  if (typeof raw === "number") return { isNull: false, value: raw };
  return { isNull: false, value: String(raw).toLowerCase() };
}

export function DataTable<T extends { id: string }>({
  columns,
  data,
  isLoading,
  total,
  page,
  perPage,
  onPageChange,
  onPerPageChange,
  onRowClick,
  emptyIcon,
  emptyTitle = "Nenhum registro encontrado",
  emptyDescription,
  emptyActionLabel,
  onEmptyAction,
  minWidth,
  ariaLabel,
  skeletonColumns,
  headerRow,
  renderRow,
  showPagination,
  selectedIds,
  onTogglePageSelection,
  selectionGetId,
  getRowClassName,
  density: densityProp,
  linhaComElementoInterativo,
  personalizacao,
}: DataTableProps<T>) {
  const hasPagination = showPagination ?? !!(page && onPageChange);
  const effectiveTotal = total ?? data.length;
  const getRowId = useCallback(
    (row: T) => (selectionGetId ?? ((r: T) => (r as { id: string }).id))(row),
    [selectionGetId],
  );
  const personalizeTrigger = personalizacao ? <TableViewControls tableView={personalizacao} /> : null;
  const tabelaVisivel = !isLoading && data.length > 0;

  /**
   * O botao de personalizar fica SOBRE a ponta direita do cabecalho, e nao
   * dentro da ultima `<th>`. Dentro da celula ele rolava junto com a tabela:
   * em listagem mais larga que a tela (Credenciados no computador, quase todas
   * a 375 px) sumia ate o usuario rolar para o lado — e a barra acima da tabela
   * que ele substituiu nunca sumia. Sobreposto, fica onde o padrao manda quando
   * a tabela cabe, e continua a vista quando nao cabe. Vem antes da tabela no
   * DOM: pelo teclado, e alcancado antes das linhas, como uma barra seria.
   *
   * A altura vem do proprio `<thead>`, que cresce quando um rotulo quebra linha.
   */
  const cabecalhoRef = useRef<HTMLTableSectionElement>(null);
  const [alturaDoCabecalho, setAlturaDoCabecalho] = useState<number | null>(null);
  useLayoutEffect(() => {
    const cabecalho = cabecalhoRef.current;
    if (!personalizacao || !tabelaVisivel || !cabecalho) return;
    const medir = () => setAlturaDoCabecalho(cabecalho.getBoundingClientRect().height || null);
    medir();
    if (typeof ResizeObserver === "undefined") return;
    const observador = new ResizeObserver(medir);
    observador.observe(cabecalho);
    return () => observador.disconnect();
  }, [personalizacao, tabelaVisivel]);
  const { density: globalDensity } = useTableDensity();
  const density = densityProp ?? globalDensity;
  const isCompact = density === "compact";

  const [sort, setSort] = useState<SortState>({ column: null, direction: null });

  const handleSort = (columnId: string) => {
    setSort((prev) => {
      if (prev.column !== columnId) return { column: columnId, direction: "asc" };
      if (prev.direction === "asc") return { column: columnId, direction: "desc" };
      return { column: null, direction: null };
    });
  };

  const sortedData = useMemo(() => {
    if (!sort.column || !sort.direction) return data;
    const col = columns.find((c) => getColumnId(c) === sort.column && c.sortKey);
    if (!col?.sortKey) return data;
    const key = col.sortKey;
    const dir = sort.direction === "asc" ? 1 : -1;
    return [...data].sort((a, b) => {
      const va = getSortValue(a, key);
      const vb = getSortValue(b, key);
      // Nulls always last regardless of direction
      if (va.isNull && !vb.isNull) return 1;
      if (!va.isNull && vb.isNull) return -1;
      if (va.isNull && vb.isNull) return 0;
      if (va.value < vb.value) return -1 * dir;
      if (va.value > vb.value) return 1 * dir;
      return 0;
    });
  }, [data, sort, columns]);

  const SortIcon = ({ columnId }: { columnId: string }) => {
    if (sort.column !== columnId) return <ArrowUpDown className="h-3.5 w-3.5 text-muted-foreground/70" />;
    if (sort.direction === "asc") return <ArrowUp className="h-3.5 w-3.5 text-foreground" />;
    return <ArrowDown className="h-3.5 w-3.5 text-foreground" />;
  };

  // Header tri-state: usa as linhas efetivamente visíveis (após ordenação/paginação aplicada pelo chamador)
  const hasBulkSelection = !!onTogglePageSelection && Array.isArray(selectedIds);
  const pageRowIds = useMemo(() => sortedData.map((r) => getRowId(r)), [sortedData, getRowId]);
  const selectedSet = useMemo(() => new Set(selectedIds ?? []), [selectedIds]);
  const selectedOnPage = pageRowIds.filter((id) => selectedSet.has(id)).length;
  const allOnPageSelected = hasBulkSelection && pageRowIds.length > 0 && selectedOnPage === pageRowIds.length;
  const someOnPageSelected = hasBulkSelection && selectedOnPage > 0 && selectedOnPage < pageRowIds.length;

  return (
    // `min-w-0`: sem isto, um `<table>` mais largo que o espaço disponível
    // (via `minWidth`) empurra o `Card` — e cada ancestral `flex-col` acima
    // dele, até `<main>` (nucleo/navegacao/CascaDeArea.tsx) — a nunca
    // encolher abaixo da largura da tabela, em vez de só rolar por dentro do
    // próprio contêiner (mesma armadilha de `min-width: auto` documentada em
    // `ui/base/chart.tsx`, achada primeiro em 375 px nos gráficos de
    // Métricas; aqui aparece em largura de desktop porque é a primeira
    // tabela com `minWidth` perto do teto de `max-w-[1400px]` da página).
    <Card className="min-w-0">
      <CardContent className="p-0">
        {isLoading ? (
          // O esqueleto é só desenho: o leitor de tela ouve que a lista carrega.
          <div role="status">
            <TableSkeleton columns={skeletonColumns ?? columns.length} rows={8} />
            <p className="sr-only">Carregando a lista.</p>
          </div>
        ) : data.length === 0 ? (
          <EmptyState
            icon={emptyIcon}
            title={emptyTitle}
            description={emptyDescription}
            actionLabel={emptyActionLabel}
            onAction={onEmptyAction}
          />
        ) : (
          <>
            {
              // Sem tabIndex/role aqui: `ui/base/table.tsx` já embrulha a
              // `<table>` no mesmo tipo de contêiner rolável ("scrollable
              // region" da WAI-ARIA, axe: scrollable-region-focusable) — os
              // dois nascem sempre juntos (este `<div>` só existe pra
              // acomodar `minWidth`). Repetir tabIndex/role aqui duplicaria
              // a landmark "region" com o mesmo nome, uma logo dentro da
              // outra — pior pra quem navega por landmark do que a ausência.
            }
            <div className="relative">
            {personalizeTrigger && (
              // `rounded-tr-xl`, o mesmo raio do `Card`: sem ele o fundo quadrado
              // desta faixa cobre o canto arredondado da borda do cartão.
              <div
                className="bg-card absolute right-0 top-0 z-10 flex items-center rounded-tr-xl pl-1 pr-3"
                style={{ height: alturaDoCabecalho ?? 48 }}
              >
                {/* Degradê: o rótulo que rola por baixo some aos poucos, em vez de ser cortado no meio da palavra. */}
                <span
                  className="from-card pointer-events-none absolute right-full top-0 h-full w-6 bg-linear-to-l to-transparent"
                  aria-hidden="true"
                />
                {personalizeTrigger}
              </div>
            )}
            <div className="overflow-x-auto">
              <Table className={minWidth ? `min-w-[${minWidth}]` : undefined} style={minWidth ? { minWidth } : undefined} aria-label={ariaLabel}>
                <TableHeader ref={cabecalhoRef}>
                  {headerRow ?? (
                    <TableRow>
                      {columns.map((col, i) => {
                        const columnId = getColumnId(col);
                        const sortable = !!col.sortKey;
                        const isSelectionCol =
                          hasBulkSelection && (col.isSelectionColumn === true || columnId === "select");
                        const isLast = i === columns.length - 1;
                        return (
                          <TableHead
                            key={columnId}
                            className={cn(
                              col.headerClassName,
                              sortable && "cursor-pointer select-none hover:bg-muted/50 transition-colors",
                              isLast && personalizeTrigger && "pr-12"
                            )}
                            onClick={sortable ? () => handleSort(columnId) : undefined}
                            aria-sort={
                              sort.column === columnId
                                ? sort.direction === "asc" ? "ascending" : "descending"
                                : undefined
                            }
                          >
                            {isSelectionCol ? (
                              <Checkbox
                                checked={
                                  allOnPageSelected
                                    ? true
                                    : someOnPageSelected
                                      ? "indeterminate"
                                      : false
                                }
                                onCheckedChange={() => onTogglePageSelection?.()}
                                onClick={(e) => e.stopPropagation()}
                                aria-label="Selecionar todos da página"
                              />
                            ) : (
                              <span className={cn(sortable && "inline-flex items-center gap-1")}>
                                <HeaderLabel col={col} />
                                {sortable && <SortIcon columnId={columnId} />}
                              </span>
                            )}
                          </TableHead>
                        );
                      })}
                    </TableRow>
                  )}
                </TableHeader>
                <TableBody>
                  {renderRow
                    ? sortedData.map((row) => renderRow(row))
                    : sortedData.map((row) => (
                        <TableRow
                          key={row.id}
                          className={cn(
                            onRowClick && "cursor-pointer hover:bg-muted/50 focus-visible:bg-muted/60 focus-visible:outline-hidden",
                            isCompact && "[&>td]:py-1.5 [&>td]:text-xs",
                            getRowClassName?.(row),
                          )}
                          role={onRowClick && !linhaComElementoInterativo ? "button" : undefined}
                          tabIndex={onRowClick && !linhaComElementoInterativo ? 0 : undefined}
                          onClick={
                            onRowClick
                              ? (e) => {
                                  const target = e.target as HTMLElement | null;
                                  if (
                                    target &&
                                    target.closest(
                                      'button,a,input,select,textarea,label,[role="menuitem"],[role="menu"],[role="dialog"],[data-row-stop]'
                                    )
                                  ) {
                                    return;
                                  }
                                  onRowClick(row);
                                }
                              : undefined
                          }
                          onKeyDown={
                            onRowClick && !linhaComElementoInterativo
                              ? (e) => {
                                  if (e.target !== e.currentTarget) return;
                                  if (e.key === "Enter" || e.key === " ") {
                                    e.preventDefault();
                                    onRowClick(row);
                                  }
                                }
                              : undefined
                          }
                        >
                          {columns.map((col) => (
                            <TableCell key={getColumnId(col)} className={col.cellClassName}>
                              {col.cell
                                ? col.cell(row)
                                : col.accessorKey
                                  ? String(row[col.accessorKey] ?? "—")
                                  : "—"}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}

                </TableBody>
              </Table>
            </div>
            </div>
            {hasPagination && page && perPage && onPageChange && onPerPageChange && (
              <DataPagination
                page={page}
                perPage={perPage}
                total={effectiveTotal}
                onPageChange={onPageChange}
                onPerPageChange={onPerPageChange}
              />
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

/** O texto do header, com `GlossaryTerm` quando `headerTermo` estiver presente. `tooltip` continua funcionando do jeito antigo. */
function HeaderLabel<T>({ col }: { col: DataTableColumn<T> }) {
  if (col.headerTermo) {
    return (
      // stopPropagation: o <TableHead> pai ordena a coluna ao clicar; sem
      // isto, abrir o popover do glossário também dispararia a ordenação.
      <span
        role="presentation"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <GlossaryTerm termo={col.headerTermo} className="normal-case">
          {col.header}
        </GlossaryTerm>
      </span>
    );
  }
  return (
    <>
      {col.header}
      {col.tooltip && <ColumnTooltip text={col.tooltip} />}
    </>
  );
}

function ColumnTooltip({ text }: { text: string }) {
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            tabIndex={-1}
            onClick={(e) => e.stopPropagation()}
            className="inline-flex items-center text-muted-foreground hover:text-foreground transition-colors"
            aria-label={`Ajuda: ${text}`}
          >
            <HelpCircle className="h-3.5 w-3.5" />
          </button>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs text-xs">
          {text}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
